import React, { Ref, useContext } from "react";
import { GiftArticle } from "@bloomberg/gifted-article";
import { SocialShare } from "@bloomberg/media-ui-core";
import { useRegUIClient, RegUISessionDetails } from "@foundation/reg-ui-client";

import PreferUsButton from "~components/PreferUs";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import { getTwitterHandle } from "~utils/getTwitterHandle";

import {
    copyLinkHandler,
    emailShareHandler,
    facebookShareHandler,
    linkedInShareHandler,
    xShareHandler,
} from "./handlers";
import styles from "./Share.module.css";

const NEXT_PUBLIC_REGISTRATION_URL =
    process.env.NEXT_PUBLIC_REGISTRATION_URL || "";

type ShareProps = {
    className?: string;
    showLabel?: boolean;
    vertical?: boolean;
    fixed?: boolean;
    hideGiftArticleText?: boolean;
    giftArticleRef?: Ref<Element>;
    showHolidayTip?: boolean;
};

type SocialClickHandler = () => void | ((event: Event) => void);

export default function Share(props: ShareProps): React.JSX.Element | null {
    const {
        className,
        showLabel = false,
        vertical = false,
        fixed = false,
        hideGiftArticleText,
        giftArticleRef = null,
        showHolidayTip = false,
    } = props;

    const { story } = useContext(CurrentStoryContext);
    const { session } = useRegUIClient();

    const {
        brand,
        byline = "",
        headline,
        seoCanonical: canonical,
        summaryText: summary,
        twitterSocialHeadline,
        url,
        id,
    } = story;

    const twitterHandle = getTwitterHandle(brand);
    const { userInfo } = session || ({} as RegUISessionDetails);

    return (
        <div className={className}>
            <SocialShare
                showLabel={showLabel}
                vertical={vertical}
                copiedToastClassName={styles.copiedToast}
                socials={{
                    email: ((event: Event) =>
                        emailShareHandler({
                            byline,
                            canonical,
                            headline,
                            summary,
                            event,
                            userInfo,
                        })) as SocialClickHandler,
                    facebook: ((event: Event) =>
                        facebookShareHandler({
                            canonical,
                            event,
                            userInfo,
                        })) as SocialClickHandler,
                    link: (() =>
                        copyLinkHandler({
                            canonical,
                        })) as SocialClickHandler,
                    linkedin: ((event: Event) =>
                        linkedInShareHandler({
                            canonical,
                            headline,
                            summary,
                            event,
                            userInfo,
                        })) as SocialClickHandler,
                    x: ((event: Event) =>
                        xShareHandler({
                            canonical,
                            twitterHandle,
                            twitterText: twitterSocialHeadline,
                            event,
                            userInfo,
                        })) as SocialClickHandler,
                }}
            />
            <GiftArticle
                ref={giftArticleRef}
                headline={headline}
                articleUrl={url}
                articleId={id}
                registrationApiUrl={NEXT_PUBLIC_REGISTRATION_URL}
                isIcon={hideGiftArticleText ?? vertical}
                fixed={fixed}
                showHolidayTip={showHolidayTip}
            />
            <PreferUsButton
                vertical={vertical}
                hideText={hideGiftArticleText}
            />
        </div>
    );
}
